import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AutoRequeueMode, IssueEnvironment, MODE_LABEL, Stage } from '../../api/issues';
import {
    applyReevaluation,
    continueReevaluation,
    exportReevaluation,
    getReevaluation,
    getReevaluations,
    nextReevaluationBatch,
    ReevaluationExample,
    ReevaluationReport,
    ReevaluationRun,
    startReevaluation,
} from '../../api/reevaluation';
import { describeError } from '../../api/requeue';
// Generic despite its name: puts text that is still being fetched on the clipboard, also in Safari and Firefox.
import { copyScenarioRunDiagnostics as copyPending } from '../../api/scenarioRunDiagnostics';
import { useApiAuth, useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { moment } from './issueFormat';
import { ReevaluateAfterDeployList } from './ReevaluateAfterDeployList';

export interface IReevaluationPanelProps {
    /** Every environment, to choose a customer or an environment from. */
    environments: IssueEnvironment[];
    /** The switch at the top of the page: only production, only test, or both. */
    stage?: Stage | '';
    /** Told after applying, so the counts on the page catch up. */
    onApplied: () => void;
    /** The customer the page was filtered on, chosen from the start; else one has to be chosen. */
    initialCustomer?: string;
    /** The environment the page was filtered on. */
    initialEnvironment?: string;
}

const OWNERS: Record<string, string> = {
    Customer: 'klant',
    Support: 'wij',
    Automatic: 'vanzelf',
    None: 'niemand',
};

/** How many rows a list shows before "toon alle". */
const SHOWN = 5;

/** The batch sizes to choose from, newest issue first; 0 is every issue. */
const BATCHES = [100, 500, 1000, 0];

const STATES: Record<string, { label: string; tone: string }> = {
    Running: { label: 'proefronde bezig', tone: 'running' },
    Done: { label: 'proefronde klaar', tone: 'ok' },
    Applying: { label: 'wordt uitgevoerd', tone: 'warn' },
    Applied: { label: 'uitgevoerd', tone: 'ok' },
};

/** A call to the API that is under way: a step can take twenty seconds, so the screen says it is busy. */
interface Pending {
    what: 'start' | 'next' | 'run' | 'apply' | 'batch';
    step: number;
    since: number;
}

const nl = (value: number) => value.toLocaleString('nl-NL');

/**
 * Every customer or every environment, as a choice of its own: an empty choice is "nothing chosen yet",
 * so nobody starts a run over everything by leaving the list as it was (3-11: meant one, ran all).
 */
const ALL = '*';

/** Elapsed time as mm:ss. */
const clock = (ms: number) => {
    const seconds = Math.max(0, Math.floor(ms / 1000));

    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

const pendingLabel = (pending: Pending, now: number) =>
    `${pending.what === 'apply' ? 'Uitvoeren loopt' : 'Proefronde loopt'}… stap ${pending.step} · ${clock(now - pending.since)}`;

/**
 * Goes through the whole backlog once: what can be closed now and why, what today's rules file differently,
 * and what was closed on a fact that no longer holds. First a dry run that only reads, a step at a time;
 * then, after reading the report, "Uitvoeren" does exactly what it showed.
 */
export const ReevaluationPanel = ({
    environments,
    stage,
    onApplied,
    initialCustomer,
    initialEnvironment,
}: IReevaluationPanelProps) => {
    const anyAxios = useAxiosSwagger();
    const { loading: tokenLoading } = useApiAuth();
    const axios = tokenLoading ? undefined : anyAxios;

    const [run, setRun] = useState<ReevaluationRun | null>(null);
    const [customer, setCustomer] = useState<string>(initialCustomer ?? '');
    const [environment, setEnvironment] = useState<string>(initialCustomer ? initialEnvironment ?? '' : '');
    const [batch, setBatch] = useState<number>(500);
    const [pending, setPending] = useState<Pending | null>(null);
    const [now, setNow] = useState<number>(() => Date.now());
    const [copied, setCopied] = useState<'busy' | 'done' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const stop = useRef<boolean>(false);
    const busy = pending !== null;

    useEffect(() => {
        if (!axios) {
            return;
        }

        const controller = new AbortController();

        getReevaluations(axios, controller.signal)
            .then((runs) =>
                runs.length > 0
                    ? getReevaluation(axios, runs[0].runId, controller.signal)
                    : null
            )
            .then((latest) => latest && setRun(latest))
            .catch((ex) => {
                if (!controller.signal.aborted) {
                    setError(describeError(ex));
                }
            });

        return () => controller.abort();
    }, [axios]);

    // The clock on the busy button, once a second while a call runs.
    useEffect(() => {
        if (!pending) {
            return;
        }

        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 1000);

        return () => window.clearInterval(timer);
    }, [pending]);

    const customers = useMemo(
        () =>
            [...new Set(environments.map((e) => e.customer))].sort((a, b) =>
                a.localeCompare(b)
            ),
        [environments]
    );
    const ofCustomer = useMemo(
        () =>
            environments
                .filter((e) => e.customer === customer)
                .map((e) => e.environment)
                .sort((a, b) => a.localeCompare(b)),
        [environments, customer]
    );

    /** Runs one call, or keeps calling while `again` says so and nobody pressed stop; busy from the click on. */
    const work = useCallback(
        async (
            what: Pending['what'],
            firstStep: number,
            call: () => Promise<ReevaluationRun>,
            again: (r: ReevaluationRun) => boolean = () => false
        ) => {
            const since = Date.now();
            let step = firstStep;

            setError(null);
            setPending({ what, step, since });
            stop.current = false;

            try {
                let next = await call();
                setRun(next);

                while (again(next) && !stop.current) {
                    step += 1;
                    setPending({ what, step, since });
                    next = await call();
                    setRun(next);
                }

                return next;
            } catch (ex) {
                setError(describeError(ex));

                return null;
            } finally {
                setPending(null);
            }
        },
        []
    );

    if (!axios) {
        return null;
    }

    const chosen = customer === ALL || (customer !== '' && environment !== '');

    const start = () =>
        work('start', 1, () =>
            startReevaluation(axios, {
                customer: customer === ALL ? undefined : customer,
                environment: customer === ALL || environment === ALL ? undefined : environment,
                stage,
                limit: batch || undefined,
            })
        );

    const nextBatch = () => {
        if (!run) {
            return;
        }

        work('batch', 1, () => nextReevaluationBatch(axios, run.runId));
    };

    const next = (toTheEnd: boolean) => {
        if (!run) {
            return;
        }

        work(
            toTheEnd ? 'run' : 'next',
            run.steps + 1,
            () => continueReevaluation(axios, run.runId),
            // A paused run waits for Exact's new day: calling on would only spin.
            (r) => toTheEnd && r.state === 'Running' && !r.pausedUntil
        );
    };

    const copy = () => {
        if (!run) {
            return;
        }

        setCopied('busy');
        setError(null);

        copyPending(exportReevaluation(axios, run.runId))
            .then(() => {
                setCopied('done');
                window.setTimeout(() => setCopied(null), 2500);
            })
            .catch((ex) => {
                setCopied(null);
                setError(`Kopiëren mislukt: ${describeError(ex)}`);
            });
    };

    const report = run?.report ?? null;
    const changes = report
        ? report.toClose + report.toReopen + report.toReclassify
        : 0;

    const apply = async () => {
        if (!run || !report) {
            return;
        }

        const question =
            `Herbeoordeling ${run.runId} uitvoeren?\n\n` +
            `${nl(report.toClose)} issues sluiten, ${nl(report.toReopen)} heropenen, ` +
            `${nl(report.toRecode)} onder een andere oorzaak zetten en bij ${nl(report.toRetext)} alleen de tekst (titel of eigenaar) bijwerken, ` +
            `elk met de reden en "herbeoordeling ${run.runId}" erbij.\n\n` +
            'Wat sinds de proefronde veranderde of waar iemand intussen iets aan deed, wordt overgeslagen. ' +
            'Er wordt niets opnieuw aangeboden of opgehaald.';

        if (!window.confirm(question)) {
            return;
        }

        const done = await work(
            'apply',
            1,
            () => applyReevaluation(axios, run.runId),
            (r) => r.state === 'Applying'
        );

        if (done) {
            onApplied();
        }
    };

    const paused = run?.state === 'Running' && !!run.pausedUntil;
    const state = run
        ? paused
            ? { label: 'gepauzeerd', tone: 'warn' }
            : STATES[run.state] ?? { label: run.state, tone: 'muted' }
        : null;
    const only =
        stage === 'prod'
            ? ', alleen productie'
            : stage === 'test'
            ? ', alleen test'
            : '';

    /** The button that started the call shows it runs, with its step and the time since the click. */
    const label = (what: Pending['what'], idle: ReactNode) =>
        pending?.what === what ? (
            <>
                <FontAwesomeIcon icon="spinner" spin /> {pendingLabel(pending, now)}
            </>
        ) : (
            idle
        );

    return (
        <section
            className={`scanner reevaluation scanner--${
                run?.state === 'Running' || busy
                    ? 'running'
                    : run?.state === 'Applying'
                    ? 'stopped'
                    : 'idle'
            }`}
            aria-label="Herbeoordeling"
            aria-busy={busy}
        >
            <div className="scanner__head">
                <div className="scanner__title">
                    <FontAwesomeIcon icon="scale-balanced" />
                    <h3>Herbeoordeling van de achterstand</h3>
                    {state && (
                        <span
                            className={`scanner__state scanner__state--${state.tone}`}
                        >
                            {state.label}
                        </span>
                    )}
                </div>
                <div className="issues__actions">
                    {run?.state === 'Running' && (
                        <>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => next(false)}
                                disabled={busy}
                                title={paused ? 'Gaat verder zodra Exact weer ruimte heeft' : undefined}
                            >
                                {label(
                                    'next',
                                    <>
                                        <FontAwesomeIcon icon="arrow-right" />{' '}
                                        Verder
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => next(true)}
                                disabled={busy || paused}
                            >
                                {label(
                                    'run',
                                    <>
                                        <FontAwesomeIcon icon="play" /> Doorlopen
                                        tot klaar
                                    </>
                                )}
                            </button>
                            {pending?.what === 'run' && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => (stop.current = true)}
                                >
                                    <FontAwesomeIcon icon="circle-stop" />{' '}
                                    Stoppen na deze stap
                                </button>
                            )}
                        </>
                    )}
                    {(run?.state === 'Done' || run?.state === 'Applying') &&
                        changes > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-warning"
                                onClick={apply}
                                disabled={busy}
                            >
                                {label(
                                    'apply',
                                    <>
                                        <FontAwesomeIcon icon="check" />{' '}
                                        {run.state === 'Applying'
                                            ? 'Uitvoeren afmaken'
                                            : `Uitvoeren (${nl(changes)})`}
                                    </>
                                )}
                            </button>
                        )}
                    {run?.more &&
                        !run.nextBatch &&
                        run.state !== 'Running' &&
                        run.state !== 'Applying' && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={nextBatch}
                                disabled={busy}
                                title="Een nieuwe proefronde van dezelfde grootte, vanaf waar deze stopte"
                            >
                                {label(
                                    'batch',
                                    <>
                                        <FontAwesomeIcon icon="layer-group" />{' '}
                                        Volgende batch
                                    </>
                                )}
                            </button>
                        )}
                    {report && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={copy}
                            disabled={copied === 'busy'}
                            title="Het hele rapport met elk issue per beslissing, om in een chat te plakken"
                        >
                            <FontAwesomeIcon
                                icon={copied === 'busy' ? 'spinner' : copied === 'done' ? 'check' : 'copy'}
                                spin={copied === 'busy'}
                            />{' '}
                            {copied === 'done' ? 'Gekopieerd' : 'Kopieer rapport (JSON)'}
                        </button>
                    )}
                </div>
            </div>

            <div className="scanner__scope">
                <label>
                    <span>Klant</span>
                    <select
                        className="form-select form-select-sm"
                        value={customer}
                        onChange={(event) => {
                            setCustomer(event.target.value);
                            setEnvironment('');
                        }}
                        disabled={busy}
                    >
                        <option value="" disabled>
                            Kies een klant…
                        </option>
                        <option value={ALL}>Alle klanten</option>
                        {customers.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                </label>
                {customer && customer !== ALL && (
                    <label>
                        <span>Omgeving</span>
                        <select
                            className="form-select form-select-sm"
                            value={environment}
                            onChange={(event) =>
                                setEnvironment(event.target.value)
                            }
                            disabled={busy}
                        >
                            <option value="" disabled>
                                Kies een omgeving…
                            </option>
                            <option value={ALL}>Alle omgevingen</option>
                            {ofCustomer.map((e) => (
                                <option key={e} value={e}>
                                    {e}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                <label>
                    <span>Batch</span>
                    <select
                        className="form-select form-select-sm"
                        value={batch}
                        onChange={(event) =>
                            setBatch(Number(event.target.value))
                        }
                        disabled={busy}
                    >
                        {BATCHES.map((b) => (
                            <option key={b} value={b}>
                                {b === 0 ? 'Alles' : `${nl(b)} nieuwste`}
                            </option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={start}
                    disabled={busy || run?.state === 'Applying' || !chosen}
                    title={
                        chosen
                            ? 'Leest de open issues en die van de laatste 30 dagen gesloten of opgelost zijn, nieuwste eerst en hooguit de gekozen batch, en zegt per issue wat er kan. Verandert niets; pas Uitvoeren doet wat het rapport laat zien.'
                            : 'Kies eerst een klant en een omgeving, of bewust "Alle".'
                    }
                >
                    {label(
                        'start',
                        <>
                            <FontAwesomeIcon icon="magnifying-glass" /> Proefronde
                            starten{only}
                        </>
                    )}
                </button>
            </div>

            <ReevaluateAfterDeployList environments={environments} stage={stage} />

            {error && <p className="issues__error">{error}</p>}

            {pending && (
                <p className="reevaluation__busy" role="status">
                    <FontAwesomeIcon icon="spinner" spin /> {pendingLabel(pending, now)}
                    {pending.what === 'run' && ' — doorlopen tot klaar; Stoppen houdt op na de stap die loopt.'}
                </p>
            )}

            {run && (
                <>
                    <Progress run={run} />

                    <p className="issues__muted">
                        Proefronde {run.runId} over {run.scope}, gestart{' '}
                        {moment(run.createdAt)}
                        {run.createdBy ? ` door ${run.createdBy}` : ''}.{' '}
                        {run.steps} stap(pen), {run.exactCalls} vragen aan
                        Exact, {run.chargebeeCalls} aan Chargebee.
                        {run.limit ? ` Batch van ${nl(run.limit)}, nieuwste eerst.` : ''}
                        {run.continuesRun ? ` Gaat verder na ${run.continuesRun}.` : ''}
                        {run.nextBatch ? ` Volgende batch: ${run.nextBatch}.` : ''}
                    </p>

                    {run.stepNote && run.state !== 'Applied' && (
                        <p className="issues__muted">
                            <FontAwesomeIcon icon="circle-info" />{' '}
                            {run.stepNote}
                        </p>
                    )}

                    {run.apply && <Applied run={run} />}

                    {report && <Report report={report} mode={run.requeueMode} />}
                </>
            )}
        </section>
    );
};

/** How far the dry run got: "x van y beoordeeld", or only that it moves for a run whose total is not known. */
const Progress = ({ run }: { run: ReevaluationRun }) => {
    const total = run.total ?? null;
    const finished = run.state !== 'Running';
    const percent =
        total === null
            ? null
            : total > 0
            ? Math.min(100, Math.round((100 * run.processed) / total))
            : finished
            ? 100
            : 0;

    return (
        <div className="scanner__progress">
            <div
                className={`scanner__bar${percent === null ? ' reevaluation__bar--unknown' : ''}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={total ?? undefined}
                aria-valuenow={run.processed}
            >
                <span style={percent === null ? undefined : { width: `${percent}%` }} />
            </div>
            <div className="scanner__progress-text">
                <strong>
                    {total === null
                        ? `${nl(run.processed)} beoordeeld`
                        : `${nl(run.processed)} van ${nl(total)} beoordeeld (${percent}%)`}
                </strong>
                <span>
                    {run.environmentsDone} van {run.environments} omgevingen
                    {run.current && !finished ? ` · volgende stap begint bij ${run.current}` : ''}
                </span>
            </div>
        </div>
    );
};

const Applied = ({ run }: { run: ReevaluationRun }) => {
    const a = run.apply!;
    const skipped =
        a.skippedChanged +
        a.skippedNotOpen +
        a.skippedPersonSet +
        a.skippedMissing;

    return (
        <p className={a.complete ? 'proposals__message' : 'scanner__note'}>
            <FontAwesomeIcon
                icon={a.complete ? 'circle-check' : 'hourglass-half'}
            />{' '}
            {a.complete ? 'Uitgevoerd' : 'Deels uitgevoerd'}
            {a.appliedBy ? ` door ${a.appliedBy}` : ''} op {moment(a.appliedAt)}
            : {a.closed} gesloten, {a.reopened} heropend, {a.reclassified}{' '}
            anders ingedeeld.
            {skipped > 0 &&
                ` Overgeslagen: ${a.skippedChanged} veranderd sinds de proefronde, ${a.skippedNotOpen} met een andere status, ` +
                    `${a.skippedPersonSet} waar iemand intussen iets aan deed, ${a.skippedMissing} verdwenen.`}
        </p>
    );
};

type SectionKey = 'close' | 'recode' | 'retext' | 'reopen' | 'other' | 'open' | 'unchecked';

const Report = ({ report, mode }: { report: ReevaluationReport; mode?: string | null }) => {
    const recoded = report.reclassified.filter((r) => !r.textOnly);
    const retexted = report.reclassified.filter((r) => r.textOnly);
    const sum = (counts: number[]) => counts.reduce((a, b) => a + b, 0);

    const sizes: Record<SectionKey, number> = {
        close: report.toClose,
        recode: sum(recoded.map((r) => r.count)),
        retext: sum(retexted.map((r) => r.count)),
        reopen: report.reopen.count,
        other: report.personSet.count + report.undecided.count,
        open: sum(report.open.map((c) => c.count)),
        unchecked: sum(report.withoutChecker.map((c) => c.count)),
    };
    // Only the biggest section opens by itself; the rest is a click away.
    const biggest = (Object.keys(sizes) as SectionKey[]).reduce((a, b) => (sizes[b] > sizes[a] ? b : a));
    const modeLabel = mode ? MODE_LABEL[mode as AutoRequeueMode] ?? mode : null;

    return (
        <div className="reevaluation__report">
            <div className="scanner__tiles">
                <Tile
                    value={report.examined}
                    label="beoordeeld"
                    tone="info"
                    hint={`Waarvan ${report.settled} gesloten of opgelost in de laatste 30 dagen. De tegels hiernaast tellen samen op tot dit getal.`}
                />
                <Tile value={report.toClose} label="sluiten" tone="ok" hint="Open issues met bewijs dat er niets meer hoeft." />
                <Tile value={report.toRecode} label="andere oorzaak" tone="info" hint="De regels van nu delen de fout onder een andere oorzaak in." />
                <Tile
                    value={report.toRetext}
                    label="alleen tekst bijwerken"
                    tone="muted"
                    hint="Zelfde oorzaak; alleen de titel of eigenaar die de regel geeft is veranderd."
                />
                <Tile value={report.toReopen} label="heropenen" tone="warn" hint="Gesloten of opgeloste issues waarvan de reden niet meer klopt." />
                <Tile value={report.keepOpen} label="blijft open" tone="muted" hint="Nog geen bewijs dat het opgelost is." />
                <Tile
                    value={report.unknown}
                    label="niet te beoordelen"
                    tone="warn"
                    hint="De koppeling of de controle was niet te lezen; een volgende proefronde probeert het opnieuw."
                />
                <Tile
                    value={report.personSet.count}
                    label="door iemand ingesteld"
                    tone="muted"
                    hint="Iemand heeft hier iets aan beslist; dat blijft zo en wordt alleen gemeld."
                />
                <Tile
                    value={report.unchanged}
                    label="blijft dicht"
                    tone="muted"
                    hint="Gesloten of opgeloste issues van de laatste 30 dagen die opnieuw bekeken zijn en dicht blijven."
                />
            </div>

            <Section title="Sluiten" count={sizes.close} open={biggest === 'close'} empty="Niets om te sluiten.">
                <Limited
                    items={report.rules}
                    render={(r) => <Row key={r.rule} title={r.label} count={r.count} examples={r.examples} />}
                />
            </Section>

            <Section
                title="Andere oorzaak"
                count={sizes.recode}
                open={biggest === 'recode'}
                empty="Geen issue dat de regels van nu onder een andere oorzaak zetten."
            >
                {report.nowRecognised > 0 && <p className="issues__muted">{report.nowRecognised} onbekende fout(en) herkent een regel nu.</p>}
                <Limited
                    items={recoded}
                    render={(r) => (
                        <Row
                            key={`${r.from}>${r.to}`}
                            title={
                                <>
                                    <code>{r.from ?? '(geen)'}</code> → <code>{r.to}</code>{' '}
                                    {r.title && <span className="issues__muted">{r.title}</span>}
                                    {r.nowRecognised && <span className="scanner__state scanner__state--ok">nu herkend</span>}
                                </>
                            }
                            count={r.count}
                            examples={r.examples}
                        />
                    )}
                />
            </Section>

            <Section
                title="Alleen tekst bijwerken"
                count={sizes.retext}
                open={biggest === 'retext'}
                empty="Alle titels en eigenaren zijn zoals de regels van nu ze geven."
                muted
            >
                <p className="issues__muted">Zelfde oorzaak; Uitvoeren zet alleen de titel en eigenaar gelijk met de regel van nu.</p>
                <Limited
                    items={retexted}
                    render={(r) => (
                        <Row
                            key={`${r.from}>${r.to}`}
                            title={
                                <>
                                    <code>{r.to}</code> {r.title && <span className="issues__muted">{r.title}</span>}
                                </>
                            }
                            count={r.count}
                            examples={r.examples}
                        />
                    )}
                />
            </Section>

            <Section title="Heropenen" count={sizes.reopen} open={biggest === 'reopen'} empty="Geen sluiting die niet meer klopt.">
                <ul className="reevaluation__list">
                    <Row
                        title="Gesloten of opgelost door de scanner, maar de feiten zeggen nu iets anders"
                        count={report.reopen.count}
                        examples={report.reopen.examples}
                    />
                </ul>
            </Section>

            {sizes.other > 0 && (
                <Section title="Door iemand ingesteld of niet te beoordelen" count={sizes.other} open={biggest === 'other'}>
                    <ul className="reevaluation__list">
                        {report.personSet.count > 0 && (
                            <Row title="Door iemand ingesteld, niet aangepast" count={report.personSet.count} examples={report.personSet.examples} />
                        )}
                        {report.undecided.count > 0 && (
                            <Row title="Niet te beoordelen" count={report.undecided.count} examples={report.undecided.examples} />
                        )}
                    </ul>
                </Section>
            )}

            {report.open.length > 0 && (
                <Section title={`Blijft open per oorzaak (${report.open.length} soorten)`} count={sizes.open} open={biggest === 'open'}>
                    {modeLabel && (
                        <p className="issues__muted">
                            Wie en wat zoals het nu staat, met opnieuw aanbieden op "{modeLabel}".
                        </p>
                    )}
                    <OpenTable codes={report.open} />
                </Section>
            )}

            {report.withoutChecker.length > 0 && (
                <Section
                    title={`Zonder controle (${report.withoutChecker.length} soorten): hier loont een nieuwe controle`}
                    count={sizes.unchecked}
                    open={biggest === 'unchecked'}
                >
                    <Limited
                        items={report.withoutChecker}
                        render={(c) => (
                            <li key={c.code} className="reevaluation__row">
                                <span>
                                    {c.title ?? c.code} <code className="issues__muted">{c.code}</code>
                                </span>
                                <strong>{nl(c.count)}</strong>
                            </li>
                        )}
                    />
                </Section>
            )}
        </div>
    );
};

const OpenTable = ({ codes }: { codes: ReevaluationReport['open'] }) => {
    const [all, setAll] = useState<boolean>(false);
    const shown = all ? codes : codes.slice(0, SHOWN);

    return (
        <>
            <div className="issues__scroll">
                <table className="issues__table">
                    <thead>
                        <tr>
                            <th>Oorzaak</th>
                            <th className="issues__num">Open</th>
                            <th>Wie</th>
                            <th>Wat moet er gebeuren</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((c) => (
                            <tr key={c.code}>
                                <td>
                                    {c.title ?? c.code}
                                    <br />
                                    <code className="issues__muted">{c.code}</code>
                                </td>
                                <td className="issues__num" title={c.unknown > 0 ? `${c.unknown} niet te beoordelen` : undefined}>
                                    {nl(c.count)}
                                </td>
                                <td>{c.owner ? OWNERS[c.owner] ?? c.owner : '–'}</td>
                                <td>{c.action ?? '–'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ShowAll total={codes.length} all={all} onToggle={() => setAll(!all)} />
        </>
    );
};

/** A part of the report, folded away unless it is the biggest. */
const Section = ({
    title,
    count,
    open,
    empty,
    muted,
    children,
}: {
    title: string;
    count: number;
    open: boolean;
    empty?: string;
    muted?: boolean;
    children: ReactNode;
}) => {
    const [expanded, setExpanded] = useState<boolean>(open);

    if (count === 0 && empty) {
        return (
            <p className="issues__muted reevaluation__empty">
                <strong>{title}:</strong> {empty}
            </p>
        );
    }

    return (
        <details className={`reevaluation__section${muted ? ' reevaluation__section--muted' : ''}`} open={expanded} onToggle={(e) => setExpanded(e.currentTarget.open)}>
            <summary className="reevaluation__heading">
                {title} <span className="issues__muted">({nl(count)})</span>
            </summary>
            {children}
        </details>
    );
};

/** The first few of a list, with "toon alle n" for the rest. */
const Limited = <T,>({ items, render }: { items: T[]; render: (item: T) => ReactNode }) => {
    const [all, setAll] = useState<boolean>(false);

    return (
        <>
            <ul className="reevaluation__list">{(all ? items : items.slice(0, SHOWN)).map(render)}</ul>
            <ShowAll total={items.length} all={all} onToggle={() => setAll(!all)} />
        </>
    );
};

const ShowAll = ({ total, all, onToggle }: { total: number; all: boolean; onToggle: () => void }) =>
    total > SHOWN ? (
        <button type="button" className="btn btn-link btn-sm reevaluation__more" onClick={onToggle}>
            {all ? 'Minder tonen' : `Toon alle ${nl(total)}`}
        </button>
    ) : null;

const Row = ({
    title,
    count,
    examples,
}: {
    title: ReactNode;
    count: number;
    examples: ReevaluationExample[];
}) => (
    <li>
        <details>
            <summary className="reevaluation__row">
                <span>{title}</span>
                <strong>{nl(count)}</strong>
            </summary>
            <ul className="reevaluation__examples">
                {examples.map((e) => (
                    <li
                        key={`${e.customer}/${e.environment}/${e.objectType}/${e.relationId}`}
                    >
                        <span className="issues__muted">
                            {e.customer} / {e.environment} · {e.objectType}
                        </span>{' '}
                        <code>{e.relationId}</code>
                        {e.reason && <span> · {e.reason}</span>}
                    </li>
                ))}
                {count > examples.length && (
                    <li className="issues__muted">
                        en nog {nl(count - examples.length)}; Kopieer rapport (JSON) heeft ze allemaal
                    </li>
                )}
            </ul>
        </details>
    </li>
);

const Tile = ({
    label,
    value,
    tone,
    hint,
}: {
    label: string;
    value: number;
    tone: string;
    hint: string;
}) => (
    <div
        className={`scanner__tile scanner__tile--${
            value === 0 ? 'muted' : tone
        }`}
        title={hint}
    >
        <strong>{nl(value)}</strong>
        <span>{label}</span>
    </div>
);
